import { createFileRoute, Link } from '@tanstack/react-router';
import { LayoutDashboard, Network, MapPin, Users } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Badge } from '@/components/ui/badge';
import { ReverseLookupCard } from '@/components/cgnat/ReverseLookupCard';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { AddressSpaceHeatmap } from '@/components/dashboard/AddressSpaceHeatmap';
import { RoleBreakdown } from '@/components/dashboard/RoleBreakdown';
import { usePrefixes } from '@/hooks/use-prefixes';
import { usePools } from '@/hooks/use-cgnat';
import { useAddresses } from '@/hooks/use-addresses';
import { useAudit } from '@/hooks/use-audit';
import { cidrHostCount } from '@/lib/ip-utils';
import { formatCompact, formatNumber, formatRelative, formatTimestamp } from '@/lib/format';
import type { CgnatPool } from '@/lib/types';

export const Route = createFileRoute('/_auth/')({
  component: DashboardRoute,
});

function useKpiData() {
  const prefixes = usePrefixes();
  const addresses = useAddresses();
  const pools = usePools();

  const subscriberCount: number = (() => {
    if (!pools.data) return 0;
    return pools.data.reduce((acc: number, pool: CgnatPool) => {
      const hosts = cidrHostCount(pool.private_prefix);
      return acc + Number(hosts ?? 0n);
    }, 0);
  })();

  return { prefixes, addresses, pools, subscriberCount };
}

function DashboardRoute() {
  const { prefixes, addresses, pools, subscriberCount } = useKpiData();
  const audit = useAudit(8);

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Panel" icon={LayoutDashboard} />

      <div className="flex min-h-0 flex-1 overflow-auto">
        <div className="flex w-full flex-col gap-4 p-4 lg:flex-row">
          {/* ── Main column ─────────────────────────────────── */}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {/* KPI row */}
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
              <KpiCard
                label="Prefijos"
                value={
                  prefixes.isSuccess
                    ? formatNumber(prefixes.data.length)
                    : '—'
                }
                sublabel="redes configuradas"
                icon={Network}
                accentColor="var(--accent-cyan)"
                loading={prefixes.isPending}
              />
              <KpiCard
                label="Direcciones"
                value={
                  addresses.isSuccess
                    ? formatNumber(addresses.data.length)
                    : '—'
                }
                sublabel="IPs registradas"
                icon={MapPin}
                accentColor="var(--info)"
                loading={addresses.isPending}
              />
              <KpiCard
                label="Pools CGNAT"
                value={
                  pools.isSuccess
                    ? formatNumber(pools.data.length)
                    : '—'
                }
                sublabel="pools activos"
                icon={Network}
                accentColor="var(--success)"
                loading={pools.isPending}
              />
              <KpiCard
                label="Suscriptores CGNAT"
                value={
                  pools.isSuccess
                    ? formatCompact(subscriberCount)
                    : '—'
                }
                sublabel="hosts privados totales"
                icon={Users}
                accentColor="var(--warning)"
                loading={pools.isPending}
              />
            </div>

            {/* Heatmap */}
            <AddressSpaceHeatmap />

            {/* Role breakdown */}
            <RoleBreakdown />
          </div>

          {/* ── Right sticky column ──────────────────────────── */}
          <aside className="flex w-full shrink-0 flex-col gap-4 lg:sticky lg:top-0 lg:w-72 lg:self-start xl:w-80">
            {/* Reverse lookup */}
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Reverse lookup CGNAT
              </p>
              <ReverseLookupCard />
            </div>

            {/* Recent activity */}
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Actividad reciente
              </p>
              {audit.isPending ? (
                <div className="rounded-md border border-dashed border-border/70 py-6">
                  <p className="text-center text-xs text-muted-foreground">Cargando…</p>
                </div>
              ) : audit.isError || !audit.data || audit.data.length === 0 ? (
                <div className="rounded-md border border-dashed border-border/70 py-6">
                  <p className="text-center text-xs text-muted-foreground">
                    {audit.isError ? 'No se pudo cargar la actividad' : 'Sin actividad reciente'}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
                  {audit.data.map((ev) => (
                    <li key={ev.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <Badge variant="secondary" className="text-[10px] uppercase">
                        {ev.action}
                      </Badge>
                      <span className="truncate text-muted-foreground">{ev.resource_type}</span>
                      <span className="ml-auto truncate text-[10px] text-muted-foreground" title={ev.actor}>
                        {ev.actor}
                      </span>
                      <span
                        className="shrink-0 text-[10px] text-muted-foreground"
                        title={formatTimestamp(ev.timestamp)}
                      >
                        {formatRelative(ev.timestamp)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {/* Link to audit page once available */}
              <Link
                to="/audit"
                className="mt-1 block text-right text-[10px] text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
              >
                Ver registro completo →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
